/** Title presets — keep in sync with `member-title-options.ts`. */
export const MEMBER_TITLE_PRESETS = [
  'Mr',
  'Mrs',
  'Miss',
  'Ms',
  'Dr',
  'Rev',
  'Pastor',
  'Elder',
  'Deacon',
] as const;

const SENTENCE_CASE_FIELDS = new Set([
  'notes',
  'disabilityNotes',
  'educationBackground',
  'employmentBackground',
  'sourceDetail',
]);

const PRESERVE_CASE_FIELDS = new Set([
  'membershipNumber',
  'nationalId',
  'baptismNumber',
  'confirmationNumber',
  'photoKey',
]);

const LOWERCASE_FIELDS = new Set(['email', 'appConnectEmail']);

export const MEMBER_TEXT_FIELDS = new Set([
  'membershipNumber',
  'firstName',
  'middleName',
  'lastName',
  'maidenName',
  'title',
  'placeOfBirth',
  'motherName',
  'fatherName',
  'occupation',
  'employer',
  'educationBackground',
  'employmentBackground',
  'nationalId',
  'email',
  'appConnectEmail',
  'address',
  'city',
  'region',
  'postalAddress',
  'postalCity',
  'postalRegion',
  'hometown',
  'hometownRegion',
  'formerChurch',
  'baptismNumber',
  'baptismPlace',
  'baptismMinister',
  'confirmationNumber',
  'confirmationPlace',
  'marriagePlace',
  'marriageMinister',
  'spouseNameManual',
  'emergencyContactName',
  'emergencyContactRelationship',
  'disabilityNotes',
  'notes',
]);

const capitalizeWord = (word: string): string => {
  if (!word) return word;
  if (/^\d/.test(word)) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const normalizeTitleCase = (text: string): string => {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (!trimmed) return trimmed;

  return trimmed
    .split(' ')
    .map((word) =>
      word
        .split('-')
        .map((part) =>
          part
            .split("'")
            .map((piece) => capitalizeWord(piece))
            .join("'"),
        )
        .join('-'),
    )
    .join(' ');
};

export const normalizeSentenceCase = (text: string): string => {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

export const normalizeMemberTitle = (title: string): string => {
  const trimmed = title.trim();
  if (!trimmed) return trimmed;
  const preset = MEMBER_TITLE_PRESETS.find((value) => value.toLowerCase() === trimmed.toLowerCase());
  return preset ?? normalizeTitleCase(trimmed);
};

export const normalizeMemberTextField = (
  field: string,
  value: unknown,
): string | null | undefined => {
  if (value === null || value === undefined) return value as null | undefined;

  const text = String(value).trim();
  if (!text) return null;

  if (LOWERCASE_FIELDS.has(field)) return text.toLowerCase();
  if (PRESERVE_CASE_FIELDS.has(field)) return text;
  if (field === 'title') return normalizeMemberTitle(text);
  if (SENTENCE_CASE_FIELDS.has(field)) return normalizeSentenceCase(text);
  return normalizeTitleCase(text);
};
